import { affectsRecord, latestRequest } from '@/services/workspaceRefresh'
import { useActionTypes } from '@/helpers/useActionTypes'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Accordion, Alert, Button, Form, Modal, Nav, Tab } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDays, faEnvelope, faPen, faPhone, faRotateRight, faTrash, faUser } from '@fortawesome/free-solid-svg-icons'
import { StopReasonDialog } from '@/components/customers/StopReasonDialog'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { DueDateField } from '@/components/common/DueDateField'
import { Field } from '@/components/common/Field'
import { collectOrderJson, OrderFormPages } from '@/components/forms/OrderFieldInputs'
import { orderDefaults, parseStoredForm, type OrderForm } from '@/components/forms/formModel'
import { OrderHistory } from '@/components/forms/CustomerOrderHistory'
import { useAuth } from '@/context/AuthContext'
import { useCrmHub, useCrmSubscription } from '@/context/SignalRContext'
import { forThisBusiness } from '@/helpers/businessName'
import { adminRoles } from '@/helpers/roles'
import type { Contact, CrmAction, CrmUser, Note } from '@/models/api'
import { readError } from '@/services/apiClient'
import {
  closeAction,
  reopenAction,
  reassignAction,
  rescheduleActions,
  saveAction,
} from '@/services/actionService'
import {
  loadCustomer,
  loadCustomerOrders,
  loadForm,
  loadOrderForms,
  placeOrder,
  deleteCustomerNote,
  loadCustomerNotes,
  saveCustomerNote,
  type CustomerDetail,
  type CustomerOrder,
  type OrderFormOption,
} from '@/services/customerService'

type Pane = 'complete' | 'add' | 'order' | 'reschedule' | 'reassign'

const paneTitles: Record<Pane, string> = {
  complete: 'Complete Action',
  add: 'Add Customer Action',
  order: 'New submission',
  reschedule: 'Reschedule Action',
  reassign: 'Reassign Action',
}

export function LiveActionWorkspace({
  action,
  users,
  onClose,
  onChanged,
}: {
  action: CrmAction
  users: CrmUser[]
  onClose: () => void
  onChanged: () => void
}) {
  useCrmSubscription(`customer:${action.customerId}`)
  useCrmSubscription('customer')
  const { session } = useAuth()
  const { notify, viewAction, subscribe } = useCrmHub()
  const [current, setCurrent] = useState(action)
  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [orders, setOrders] = useState<CustomerOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [pane, setPane] = useState<Pane | null>(null)
  const [tab, setTab] = useState('details')
  const [imageOpen, setImageOpen] = useState(false)
  const [addingNote, setAddingNote] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [notePage, setNotePage] = useState(1)
  const [noteRows, setNoteRows] = useState<Note[]>([])
  const [noteTotal, setNoteTotal] = useState(0)
  const [noteVersion, setNoteVersion] = useState(0)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [removingStop, setRemovingStop] = useState(false)
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState(false)
  const [actionText, setActionText] = useState(action.actionDetail)
  const [busy, setBusy] = useState(false)


  useEffect(() => {
    if (current.dateClosed) return
    return viewAction(current.id)
  }, [current.id, current.dateClosed, viewAction])

  const refreshRequest = useRef(latestRequest())
  useEffect(() => () => { refreshRequest.current() }, [action.customerId])
  const openActionId = useRef(current.id)
  openActionId.current = current.id
  const sessionRef = useRef(session)
  sessionRef.current = session
  const usersRef = useRef(users)
  usersRef.current = users

  useEffect(() => subscribe((event) => {
    if (affectsRecord(event, 'customer', 'customer', action.customerId) && event.name === 'WorkspaceChanged') {
      const latest = refreshRequest.current()
      void Promise.all([loadCustomer(action.customerId), loadCustomerOrders(action.customerId)])
        .then(([row, nextOrders]) => { if (!latest()) return; setCustomer(row); setOrders(nextOrders); setNoteVersion(value => value + 1); const next = row.actions.find(item => item.id === openActionId.current); if (next) setCurrent(next) })
        .catch(caught => { if (latest()) setError(readError(caught)) })
        .finally(() => { if (latest()) setLoading(false) })
      return
    }
    if (event.name !== 'ActionClosed' && event.name !== 'ActionClosedWithNew') return
    const actionId = eventActionId(event.payload)
    if (!actionId || !sameId(actionId, openActionId.current)) return
    const signedIn = sessionRef.current
    const closedBy = closedByName(event.payload, signedIn?.userId, signedIn?.fullName || signedIn?.emailAddress, usersRef.current)
    setCurrent((row) => row.dateClosed ? row : {
      ...row,
      dateClosed: new Date().toISOString(),
      status: 'CLOSED',
      closedBy: closedBy || row.closedBy,
    })
    setPane(null)
  }), [subscribe, action.customerId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const latest = refreshRequest.current()
    Promise.all([loadCustomer(action.customerId), loadCustomerOrders(action.customerId)])
      .then(([nextCustomer, nextOrders]) => {
        if (cancelled || !latest()) return
        setCustomer(nextCustomer)
        setOrders(nextOrders)
      })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
      .finally(() => { if (!cancelled && latest()) setLoading(false) })
    return () => { cancelled = true }
  }, [action.customerId])

  useEffect(() => {
    setNotePage(1)
    setNoteRows([])
    setNoteTotal(0)
    setEditingNoteId(null)
    setAddingNote(false)
  }, [action.customerId])

  useEffect(() => {
    if (!customer || customer.id !== action.customerId) return
    let cancelled = false
    void loadCustomerNotes(customer.id, notePage, notePageSize)
      .then((result) => {
        if (cancelled) return
        const pages = Math.max(1, Math.ceil(result.total / notePageSize))
        if (notePage > pages) {
          setNotePage(pages)
          return
        }
        setNoteRows(result.notes)
        setNoteTotal(result.total)
      })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [customer?.id, action.customerId, notePage, noteVersion])

  const phone = customer ? contactPhone(customer) : ''
  const email = customer ? contactEmail(customer) : ''
  const closed = Boolean(current.dateClosed)

  async function run(task: () => Promise<void>, onError?: (caught: unknown) => void) {
    setBusy(true)
    setError(null)
    try {
      await task()
      onChanged()
    } catch (caught) {
      if (onError) onError(caught)
      else setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
    <Modal
      show
      onHide={() => { if (!busy) onClose() }}
      keyboard={!busy}
      backdrop="static"
      backdropClassName="action-workspace-backdrop"
      aria-label={`Action details for ${current.customerName ?? 'customer'}`}
      className="action-workspace-modal"
      dialogClassName="action-workspace-dialog"
      contentClassName="action-workspace-content"
      container={() => document.querySelector('.admin-shell') as HTMLElement}
    >
      <div inert={busy} aria-busy={busy} className={`action-workspace ${pane ? 'action-workspace--paired' : ''}`}>
        {pane && customer && (
          <section className="action-workspace-pane action-workspace-pane--work" aria-labelledby="action-workflow-title">
            <header className="action-workspace-header">
              <h2 tabIndex={-1} id="action-workflow-title">{paneTitles[pane]}</h2>
              <Button variant="link" className="btn-close" aria-label="Close action pane" disabled={busy} onClick={() => setPane(null)} />
            </header>
            <Workflow
              key={pane}
              pane={pane}
              action={current}
              customer={customer}
              users={users}
              busy={busy}
              onCancel={() => setPane(null)}
              onSubmit={(task, onError) => void run(task, onError)}
              onDone={(text, next) => {
                if (next) setCurrent(next)
                setMessage(text)
                setPane(null)
                if (text === 'Form submitted.') {
                  setTab('orders')
                  void loadCustomerOrders(customer.id).then(setOrders).catch(() => undefined)
                }
              }}
            />
          </section>
        )}
        <section className="action-workspace-pane action-workspace-pane--details" aria-label="Action details">
          <Tab.Container activeKey={tab} onSelect={(key) => key && setTab(key)}>
            <header className="action-workspace-header">
              <Nav variant="pills" aria-label="Action detail tabs">
                {[['details', 'Details'], ['contacts', 'Contacts'], ['orders', 'Submissions'], ['notes', 'Notes']].map(([key, label]) => (
                  <Nav.Item key={key}>
                    <Nav.Link eventKey={key} disabled={busy || (addingNote && key !== 'notes')}>{label}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              <Button variant="link" className="btn-close" aria-label="Close action details" disabled={busy} onClick={onClose} />
            </header>
            <div className="action-workspace-scroll">
              {loading && <LoadingSpinner text="Loading action" size="3x" />}
              {error && <Alert variant="danger">{error}</Alert>}
              {message && <Alert variant="success" role="status">{message}</Alert>}
              {customer && !loading && (
                <>
                  {customer.onStop && <Alert variant="danger" className="text-center fw-semibold">ACCOUNT IS ON STOP</Alert>}
                  {closed && <Alert variant="info">Action completed{current.closedBy ? ` by ${current.closedBy}` : ''}. <Button size="sm" disabled={busy} onClick={() => void run(async () => { await reopenAction(current.id); setCurrent(row => ({ ...row, dateClosed: null, status: 'PENDING', closedBy: null })) })}>Reopen action</Button></Alert>}
                  <Tab.Content>
                    <Tab.Pane eventKey="details">
                      <div className="action-facts action-facts--three">
                        <Fact label="Account Number">{customer.accountNumber || 'Not set'}</Fact>
                        <Fact label="Account Owner">{customer.accountOwnerName || 'Not set'}</Fact>
                        <Fact label="Customer Type">{customer.customerType || 'Not set'}</Fact>
                      </div>
                      <hr />
                      <div className="action-customer-heading">
                        <div>
                          <Fact label="Customer"><h3 className="h4">{customer.name}</h3></Fact>
                          <Fact label="Contact">{defaultContact(customer)?.fullname || customer.name}</Fact>
                        </div>
                        {customer.imageUrl && (
                          <button type="button" className="customer-image-button" onClick={() => setImageOpen(true)} aria-label="View customer image">
                            <img className="customer-image" src={customer.imageUrl} alt={customer.name} />
                          </button>
                        )}
                      </div>
                      <hr />
                      <div className="action-facts">
                        <Fact label="Next Action On">{formatWhen(current.actionDate)}</Fact>
                        <Fact label="Status (click to view)">
                          <Link to={`/customers/${customer.id}?t=action`} target="_blank" rel="noreferrer" className={`btn btn-sm btn-outline-${current.status === 'OVERDUE' ? 'danger' : 'primary'}`}>
                            {statusLabel(current.status)}
                          </Link>
                        </Fact>
                      </div>
                      <hr />
                      <div className="action-facts">
                        <Fact label="Address">{addressLines(customer)}</Fact>
                        <div>
                          <Fact label="Contact Number">
                            {phone ? <a href={`tel:${phone}`}><FontAwesomeIcon icon={faPhone} /> {phone}</a> : 'Not set'}
                          </Fact>
                          <Fact label="Email Address">
                            {email ? <a href={`mailto:${email}`}>{email}</a> : 'Not set'}
                          </Fact>
                        </div>
                      </div>
                      <div className="action-facts action-facts--four">
                        <Fact label="Assigned To">{current.assignedToName || 'Unassigned'}</Fact>
                        <Fact label="Category">{customer.category || current.customerCategory || 'Not set'}</Fact>
                        <Fact label="Type Of Action">{current.actionType}</Fact>
                        <Fact label="Preferred Action">{customer.preferredContactMethod || 'Not set'}</Fact>
                      </div>
                      <div className="action-facts action-facts--four">
                        <Fact label="Call Day">{customer.callDay || 'Not set'}</Fact>
                        <Fact label="Delivery Day">{customer.deliveryDay || 'Not set'}</Fact>
                        <Fact label="Discount">{customer.discount || 'Not set'}</Fact>
                        <Fact label="Grid Reference">{customer.gridRef || 'Not set'}</Fact>
                      </div>
                      <hr />
                      {editing ? (
                        <Form onSubmit={(event) => {
                          event.preventDefault()
                          void run(async () => {
                            await saveAction({
                              id: current.id,
                              customerId: current.customerId,
                              assignedToId: current.assignedToId,
                              actionType: current.actionType,
                              actionDetail: actionText,
                              actionDate: current.actionDate,
                            })
                            setCurrent({ ...current, actionDetail: actionText })
                            setEditing(false)
                            setMessage('Action text saved.')
                          })
                        }}>
                          <Field label="Action text" type="textarea" value={actionText} onChange={setActionText} required />
                          <Button type="submit" size="sm" disabled={busy}>Save action text</Button>{' '}
                          <Button variant="outline-secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                        </Form>
                      ) : (
                        <>
                          <div className="d-flex align-items-center gap-2">
                            <span className="form-label mb-0">Action</span>
                            {!closed && (
                              <Button size="sm" variant="link" aria-label="Edit action text" onClick={() => { setActionText(current.actionDetail); setEditing(true) }}>
                                <FontAwesomeIcon icon={faPen} />
                              </Button>
                            )}
                          </div>
                          <p className="action-text">{current.actionDetail}</p>
                        </>
                      )}
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        <Button size="sm" variant="outline-primary" disabled={closed} onClick={() => setPane('reschedule')}><FontAwesomeIcon icon={faCalendarDays} /> Reschedule</Button>
                        <Button size="sm" variant="outline-primary" disabled={closed} onClick={() => setPane('reassign')}><FontAwesomeIcon icon={faUser} /> Reassign</Button>
                        <Button size="sm" variant="outline-primary" onClick={() => setPane('add')}>Add action</Button>
                      </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="contacts">
                      <h3 className="h5">Customer contacts</h3>
                      {customer.contacts.length === 0 && <p className="text-body-secondary">No contacts recorded.</p>}
                      {customer.contacts.map((contact) => (
                        <div className="activity" key={contact.id}>
                          <strong>{contactLabel(contact)}</strong>
                          {contact.position && <div>{contact.position}</div>}
                          <p className="mt-2 mb-0">
                            {contact.defaultOrFirstContactNumber && <><a href={`tel:${contact.defaultOrFirstContactNumber}`}>{contact.defaultOrFirstContactNumber}</a><br /></>}
                            {contact.defaultOrFirstEmailAddress && <a href={`mailto:${contact.defaultOrFirstEmailAddress}`}>{contact.defaultOrFirstEmailAddress}</a>}
                          </p>
                        </div>
                      ))}
                    </Tab.Pane>
                    <Tab.Pane eventKey="orders">
                      <h3 className="h5">Previous submissions</h3>
                      {orders.length === 0 && <p className="text-body-secondary">No submissions recorded for this customer.</p>}
                      <OrderHistory orders={orders} customer={customer} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="notes">
                      {addingNote && (
                        <Form className="mb-3" onSubmit={(event) => {
                          event.preventDefault()
                          void run(async () => {
                            const savedId = await saveCustomerNote(customer.id, note)
                            await notify('SendNoteAdded', { organisationId: customer.id, noteId: savedId, userId: session?.userId, text: note })
                            setAddingNote(false)
                            setNote('')
                            setNotePage(1)
                            setNoteVersion((current) => current + 1)
                            setMessage('Note details saved.')
                          })
                        }}>
                          <h3 className="h5" tabIndex={-1}>Add note</h3>
                          <Field label="Note details" type="textarea" value={note} onChange={setNote} required />
                          <Button type="submit" disabled={busy}>Save note</Button>{' '}
                          <Button variant="outline-secondary" onClick={() => setAddingNote(false)}>Cancel</Button>
                        </Form>
                      )}
                      <NoteList
                        notes={noteRows}
                        total={noteTotal}
                        general={customer.notes}
                        page={notePage}
                        canManage={Boolean(session?.role && (adminRoles.includes(session.role) || customer.accountOwnerId === session.businessUserId))}
                        editingNoteId={editingNoteId}
                        editText={editText}
                        confirmDeleteId={confirmDeleteId}
                        busy={busy}
                        onPage={setNotePage}
                        onRefresh={() => setNoteVersion((current) => current + 1)}
                        onEdit={(item) => { setEditingNoteId(item.id); setEditText(item.noteText); setConfirmDeleteId(null) }}
                        onEditText={setEditText}
                        onCancelEdit={() => setEditingNoteId(null)}
                        onSaveEdit={(item) => {
                          const text = editText.trim()
                          if (!text) return
                          void run(async () => {
                            await saveCustomerNote(customer.id, text, item.id)
                            await notify('SendNoteUpdated', { organisationId: customer.id, noteId: item.id, userId: session?.userId, text })
                            setEditingNoteId(null)
                            setNoteVersion((current) => current + 1)
                            setMessage('Note updated.')
                          })
                        }}
                        onAskDelete={setConfirmDeleteId}
                        onDelete={(item) => {
                          void run(async () => {
                            await deleteCustomerNote(item.id)
                            await notify('SendNoteDeleted', { organisationId: customer.id, noteId: item.id, userId: session?.userId })
                            const pages = Math.max(1, Math.ceil(Math.max(noteTotal - 1, 0) / notePageSize))
                            setConfirmDeleteId(null)
                            setEditingNoteId(null)
                            setNotePage((current) => Math.min(current, pages))
                            setNoteVersion((current) => current + 1)
                            setMessage('Note deleted.')
                          })
                        }}
                      />
                    </Tab.Pane>
                  </Tab.Content>
                </>
              )}
            </div>
            {customer && !loading && !addingNote && !editingNoteId && (
              <footer className="action-workspace-footer">
                <div className="action-workspace-contact">
                  {phone ? <a href={`tel:${phone}`}><FontAwesomeIcon icon={faPhone} /> {phone}</a> : email ? <a href={`mailto:${email}`}><FontAwesomeIcon icon={faEnvelope} /> {email}</a> : <span>No contact number</span>}
                </div>
                <div className="action-workspace-buttons">
                  {!closed && <Button onClick={() => setPane('complete')}>Complete action</Button>}
                  <Button variant="outline-primary" onClick={() => { setTab('notes'); setAddingNote(true); setNote('') }}>Add note</Button>
                  {!customer.onStop ? (
                    <Button onClick={() => setPane('order')}>New submission</Button>
                  ) : session?.role && adminRoles.includes(session.role) && (
                    <Button variant="outline-success" onClick={() => setRemovingStop(true)}>Remove Stop</Button>
                  )}
                </div>
              </footer>
            )}
          </Tab.Container>
        </section>
      </div>
      {removingStop && customer && (
        <StopReasonDialog
          customerId={customer.id}
          customerName={customer.name}
          placingOnStop={false}
          onClose={() => setRemovingStop(false)}
          onDone={() => {
            void notify('SendCompanyStopChanged', { organisationId: customer.id, onStop: false, userId: session?.userId })
            void loadCustomer(customer.id).then(setCustomer).catch(() => setCustomer({ ...customer, onStop: false }))
            setMessage('Account stop removed.')
            setRemovingStop(false)
          }}
        />
      )}
    </Modal>
    {customer?.imageUrl && (
      <Modal show={imageOpen} onHide={() => setImageOpen(false)} size="lg" className="customer-lightbox" container={() => document.querySelector('.admin-shell') as HTMLElement}>
        <Modal.Body>
          <img src={customer.imageUrl} alt={customer.name} />
        </Modal.Body>
      </Modal>
    )}
    </>
  )
}

const notePageSize = 10

function NoteList({
  notes,
  total,
  general,
  page,
  canManage,
  editingNoteId,
  editText,
  confirmDeleteId,
  busy,
  onPage,
  onRefresh,
  onEdit,
  onEditText,
  onCancelEdit,
  onSaveEdit,
  onAskDelete,
  onDelete,
}: {
  notes: Note[]
  total: number
  general: string | null
  page: number
  canManage: boolean
  editingNoteId: string | null
  editText: string
  confirmDeleteId: string | null
  busy: boolean
  onPage: (page: number) => void
  onRefresh: () => void
  onEdit: (note: Note) => void
  onEditText: (value: string) => void
  onCancelEdit: () => void
  onSaveEdit: (note: Note) => void
  onAskDelete: (id: string | null) => void
  onDelete: (note: Note) => void
}) {
  const pages = Math.max(1, Math.ceil(total / notePageSize))
  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <Button type="button" variant="outline-primary" size="sm" disabled={busy} onClick={onRefresh}>
          <FontAwesomeIcon icon={faRotateRight} /> Refresh
        </Button>
        {total > 0 && (
          <div className="note-pager" aria-label="Note pages">
            <Button type="button" variant="primary" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous notes">&lt;</Button>
            <span>Page {page} of {pages}</span>
            <Button type="button" variant="primary" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next notes">&gt;</Button>
          </div>
        )}
      </div>
      <h3 className="h5">Customer notes</h3>
      {general && <div className="activity"><small>General notes</small><p>{general}</p></div>}
      {total === 0 && !general && <p className="text-body-secondary">No notes recorded.</p>}
      {notes.map((item) => (
        <div className={`activity customer-activity ${noteKindClass(item.noteType)}`} key={item.id}>
          <div className="d-flex justify-content-between gap-2">
            <small>{noteKindLabel(item.noteType)}{formatNoteDate(item.dateAdded)}{item.addedByName ? ` ${item.addedByName}` : ''}</small>
            {canManage && editingNoteId !== item.id && confirmDeleteId !== item.id && (
              <span className="note-actions customer-activity__buttons">
                <button type="button" className="btn btn-link btn-sm p-0" aria-label="Edit note" disabled={busy} onClick={() => onEdit(item)}><FontAwesomeIcon icon={faPen} /></button>
                <button type="button" className="btn btn-link btn-sm p-0 text-danger" aria-label="Delete note" disabled={busy} onClick={() => onAskDelete(item.id)}><FontAwesomeIcon icon={faTrash} /></button>
              </span>
            )}
          </div>
          {editingNoteId === item.id ? (
            <Form onSubmit={(event) => { event.preventDefault(); onSaveEdit(item) }}>
              <Form.Control as="textarea" rows={3} value={editText} onChange={(event) => onEditText(event.target.value)} aria-label="Note details" />
              <div className="d-flex gap-2 mt-2">
                <Button type="submit" size="sm" disabled={busy || !editText.trim()}>Save</Button>
                <Button type="button" variant="outline-secondary" size="sm" disabled={busy} onClick={onCancelEdit}>Cancel</Button>
              </div>
            </Form>
          ) : (
            <p className="action-text">{item.noteText}</p>
          )}
          {confirmDeleteId === item.id && (
            <div className="d-flex gap-2">
              <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => onDelete(item)}>Delete note</Button>
              <Button type="button" variant="outline-secondary" size="sm" disabled={busy} onClick={() => onAskDelete(null)}>Keep note</Button>
            </div>
          )}
        </div>
      ))}
    </>
  )
}

function formatNoteDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}-${month}-${date.getFullYear()}`
}

function noteKindClass(noteType?: string) {
  if (noteType === 'OnStop') return 'activity--on-stop'
  if (noteType === 'StopRemoved') return 'activity--stop-removed'
  return ''
}

function noteKindLabel(noteType?: string) {
  if (noteType === 'OnStop') return 'Placed on stop · '
  if (noteType === 'StopRemoved') return 'Stop removed · '
  return ''
}

function Workflow({
  pane,
  action,
  customer,
  users,
  busy,
  onCancel,
  onSubmit,
  onDone,
}: {
  pane: Pane
  action: CrmAction
  customer: CustomerDetail
  users: CrmUser[]
  busy: boolean
  onCancel: () => void
  onSubmit: (task: () => Promise<void>, onError?: (caught: unknown) => void) => void
  onDone: (text: string, next?: CrmAction) => void
}) {
  const { session } = useAuth()
  const { notify } = useCrmHub()
  const [followUp, setFollowUp] = useState(true)
  const [showTime, setShowTime] = useState(true)
  const [due, setDue] = useState(pane === 'complete' ? followUpDate(action.actionDate) : dateInputValue(action.actionDate))
  const [type, setType] = useState(action.actionType || 'Call')
  const [assignee, setAssignee] = useState(action.assignedToId)
  const [text, setText] = useState(pane === 'complete' ? action.actionDetail : '')
  const [forms, setForms] = useState<OrderFormOption[]>([])
  const [orderFormId, setOrderFormId] = useState('')
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null)
  const [orderError, setOrderError] = useState<string | null>(null)
  const actionTypes = useActionTypes()
  const types = [...new Set([action.actionType, ...actionTypes].filter(Boolean))]
  const contactName = defaultContact(customer) ? contactLabel(defaultContact(customer) as Contact) : customer.name

  useEffect(() => {
    if (pane !== 'order') return
    let cancelled = false
    loadOrderForms()
      .then((rows) => { if (!cancelled) setForms(rows.filter((item) => item.hasPublishedVersion)) })
      .catch(() => { if (!cancelled) setForms([]) })
    return () => { cancelled = true }
  }, [pane])

  useEffect(() => {
    if (!orderFormId) {
      setOrderForm(null)
      return
    }
    let cancelled = false
    setOrderForm(null)
    setOrderError(null)
    loadForm(Number(orderFormId))
      .then((row) => {
        if (!cancelled) { const parsed = parseStoredForm(row.publishedFormJson, { id: String(row.id), name: row.formName }); setOrderForm(parsed ? { ...parsed, publishedVersionId: row.currentVersionId } : null) }
      })
      .catch((caught) => { if (!cancelled) setOrderError(readError(caught)) })
    return () => { cancelled = true }
  }, [orderFormId])

  function submit(event: FormEvent) {
    event.preventDefault()
    if (pane === 'complete') {
      onSubmit(async () => {
        await closeAction(action.id, followUp ? '' : text, followUp ? {
          customerId: action.customerId,
          assignedToId: assignee,
          actionType: type,
          actionDetail: text,
          actionDate: toIso(due),
        } : undefined)
        onDone(followUp ? 'Action completed and follow-up added.' : 'Action completed.', { ...action, dateClosed: new Date().toISOString(), status: 'CLOSED', closedBy: session?.fullName ?? null })
      })
      return
    }
    if (pane === 'add') {
      onSubmit(async () => {
        const created = await saveAction({
          customerId: action.customerId,
          assignedToId: assignee,
          actionType: type,
          actionDetail: text,
          actionDate: toIso(due),
        })
        await notify('SendActionAdded', { actionId: created, organisationId: action.customerId, userId: session?.userId, text })
        onDone('New action saved.')
      })
      return
    }
    if (pane === 'reschedule') {
      onSubmit(async () => {
        const nextDate = toIso(due)
        await rescheduleActions([action.id], nextDate)
        onDone('Action rescheduled.', { ...action, actionDate: nextDate })
      })
      return
    }
    if (pane === 'reassign') {
      onSubmit(async () => {
        await reassignAction(action.id, assignee)
        const person = users.find((user) => user.crmAccountUserId === assignee)
        onDone('Action reassigned.', { ...action, assignedToId: assignee, assignedToName: person?.fullName || person?.emailAddress || null })
      })
    }
  }

  if (pane === 'order') {
    const orderBody = (page: ReactNode) => (
      <div className="action-order-layout">
        <div>
          <Form.Group controlId="order-form" className="mb-3">
            <Form.Label>Form</Form.Label>
            <Form.Select value={orderFormId} onChange={(event) => setOrderFormId(event.target.value)} required>
              <option value="">Select form</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>{form.formName}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Accordion defaultActiveKey="customer">
            <Accordion.Item eventKey="customer">
              <Accordion.Header>{customer.name}</Accordion.Header>
              <Accordion.Body>
                <p>{addressLines(customer)}{contactPhone(customer)}</p>
                <Fact label="Default Discount">{customer.discount || 'Not set'}</Fact>
                <Fact label="Call Day">{customer.callDay || 'Not set'}</Fact>
                <Fact label="Delivery Day">{customer.deliveryDay || 'Not set'}</Fact>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <div>
          {orderError && <Alert variant="danger">{orderError}</Alert>}
          {!forms.length && <Alert variant="info">No published forms are available {forThisBusiness(session)} yet.</Alert>}
          {page}
          {forms.length > 0 && !orderForm && <Alert variant="info">Choose a form. Previous submissions are on the Submissions tab.</Alert>}
        </div>
      </div>
    )
    if (!orderForm) {
      return (
        <div className="action-workflow-form">
          <div className="action-workspace-scroll">{orderBody(null)}</div>
          <footer className="action-workspace-footer d-flex justify-content-end">
            <Button variant="outline-secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
          </footer>
        </div>
      )
    }
    return (
      <OrderFormPages
        key={orderForm.id}
        form={orderForm}
        busy={busy}
        pinned
        defaults={customer ? orderDefaults(customer, session?.fullName) : undefined}
        submitLabel="Submit"
        onCancel={onCancel}
        onSubmit={(element) => {
          const orderJson = collectOrderJson(element)
          setOrderError(null)
          onSubmit(async () => {
            await placeOrder({ formId: Number(orderFormId), formVersionId: orderForm?.publishedVersionId ?? 0, customerId: action.customerId, orderJson, actionId: action.id })
            onDone('Form submitted.')
          }, caught => setOrderError(readError(caught)))
        }}
      >
        {orderBody}
      </OrderFormPages>
    )
  }

  const showNew = pane === 'add' || (pane === 'complete' && followUp)
  return (
    <Form className="action-workflow-form" onSubmit={submit}>
      <div className="action-workspace-scroll">
        {pane === 'complete' && (
          <>
            <Form.Check type="switch" id="close-with-new-action" className="my-4" label="Add new action when closing this one" checked={followUp} onChange={(event) => setFollowUp(event.target.checked)} />
            {followUp && <h3 className="h5">New action details</h3>}
          </>
        )}
        {showNew && (
          <div className="action-facts action-workflow-customer">
            <div>
              <Fact label="Customer name">{customer.name}</Fact>
              <Fact label="Contact name">{contactName}</Fact>
            </div>
            <Fact label="Address">{addressLines(customer)}</Fact>
          </div>
        )}
        <div className={showNew ? 'action-workflow-fields' : ''}>
          {showNew && <DueDateField label="Due date" value={due} onChange={setDue} required />}
          {pane === 'reschedule' && (
            <>
              <Field label="Select new date" type={showTime ? 'datetime-local' : 'date'} value={showTime ? due : due.slice(0, 10)} onChange={setDue} required />
              <Form.Check id="action-include-time" label="Include time" className="mb-3" checked={showTime} onChange={(event) => {
                setShowTime(event.target.checked)
                setDue(event.target.checked ? `${due.slice(0, 10)}T09:00` : due.slice(0, 10))
              }} />
            </>
          )}
          {showNew && <Field label="Action type" options={types} value={type} onChange={setType} />}
          {(showNew || pane === 'reassign') && (
            <Form.Group className="mb-3">
              <Form.Label>Assign to</Form.Label>
              <Form.Select value={assignee} onChange={(event) => setAssignee(event.target.value)} required>
                <option value="">Select a user</option>
                {users.map((user) => <option key={user.crmAccountUserId} value={user.crmAccountUserId}>{user.fullName || user.emailAddress}</option>)}
              </Form.Select>
            </Form.Group>
          )}
          {showNew && <Field label="Action text" type="textarea" value={text} onChange={setText} required />}
          {pane === 'complete' && !followUp && <Field label="Completion detail" type="textarea" value={text} onChange={setText} />}
        </div>
      </div>
      <footer className="action-workspace-footer d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={busy}>{pane === 'complete' ? 'Complete' : pane === 'add' ? 'Save action' : pane === 'reschedule' ? 'Reschedule' : 'Reassign'}</Button>
      </footer>
    </Form>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return <div className="action-fact"><div className="form-label">{label}</div><div>{children}</div></div>
}

function eventActionId(payload: { actionId?: string | null } | null) {
  const record = payload as Record<string, unknown> | null
  const id = payload?.actionId ?? record?.ActionId ?? record?.closedActionId ?? record?.ClosedActionId
  return typeof id === 'string' ? id : null
}

function sameId(left: string, right: string) {
  return left.toLowerCase() === right.toLowerCase()
}

function closedByName(payload: { userId?: string | null; closedBy?: string | null } | null, sessionUserId?: string | null, sessionName?: string | null, users: CrmUser[] = []) {
  const record = payload as Record<string, unknown> | null
  const raw = payload?.closedBy ?? record?.ClosedBy
  const named = typeof raw === 'string' ? raw.trim() : ''
  if (named) return named
  const userId = payload?.userId ?? (typeof record?.UserId === 'string' ? record.UserId : null)
  if (userId && sessionUserId && sameId(userId, sessionUserId)) return sessionName || null
  const user = users.find((item) => userId && sameId(item.userId, userId))
  return user?.fullName || user?.emailAddress || null
}

function statusLabel(status: string) {
  if (status === 'OVERDUE') return 'Overdue'
  if (status === 'TODAY') return 'Due today'
  if (status === 'TOMORROW') return 'Tomorrow'
  if (status === 'CLOSED') return 'Completed'
  return 'Pending'
}

function formatWhen(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function toIso(value: string) {
  const date = value.length === 10 ? new Date(`${value}T09:00`) : new Date(value)
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
}

function dateInputValue(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}

function followUpDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return dateInputValue(new Date().toISOString())
  date.setDate(date.getDate() + 7)
  return dateInputValue(date.toISOString())
}

function addressLines(customer: CustomerDetail) {
  const lines = [customer.address1, customer.address2, customer.address3, customer.city, customer.county, customer.postcode].filter(Boolean)
  return lines.length ? lines.map((line) => <span key={line}>{line}<br /></span>) : 'Not set'
}

function defaultContact(customer: CustomerDetail): Contact | undefined {
  return customer.contacts.find((contact) => contact.defaultContact) ?? customer.contacts[0]
}

function contactPhone(customer: CustomerDetail) {
  return defaultContact(customer)?.defaultOrFirstContactNumber || customer.contactNumber || ''
}

function contactEmail(customer: CustomerDetail) {
  return defaultContact(customer)?.defaultOrFirstEmailAddress || customer.emailAddress || ''
}

function contactLabel(contact: Contact) {
  return contact.fullname || [contact.title, contact.firstname, contact.lastname].filter(Boolean).join(' ')
}
